import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../../../constants';
import { BudgetPlanOverviewResponse, useAsyncAction, useTransaction } from '../../../../../hooks';
import { useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { commonTranslations, homeTranslations, soldProductsTranslations } from '../../../../../translations';
import { areaOverviewTranslations } from '../../../../../translations/areaOverview';
import { formatAmountWithSeparator, isFailureResponse, Translation } from '../../../../../utils';
import { OverviewWidget } from '../../../../areaOverview/overviewWidget/OverviewWidget';
import { AreaWidgetType } from '../../../../areaOverview/util';

enum BudgetPlanColumnType {
  Metric = 'metric',
  Budget = 'budget',
  Plan = 'plan',
}

type BudgetPlanRow = {
  metricName: Translation;
  budget: string;
  plan: string;
};

type BudgetPlanOverviewWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
};

export const BudgetPlanOverviewWidget: FC<BudgetPlanOverviewWidgetProps> = observer(
  ({ currentDate, interval }) => {
    const { t } = useTranslation();
    const { fetchBudgetPlanOverviewData } = useTransaction();
    const { getFormattedCurrency } = useCurrencyTools(interval, currentDate);

    const {
      result: budgetPlanOverviewData,
      isLoading: budgetPlanOverviewLoading,
      isError: budgetPlanOverviewError,
      triggerAction: triggerFetchBudgetPlanOverview,
    } = useAsyncAction<BudgetPlanOverviewResponse>();

    const fetchData = async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });
      const response = await fetchBudgetPlanOverviewData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    };

    const fetchWidgetData = () => {
      triggerFetchBudgetPlanOverview(fetchData);
    };

    useEffect(() => {
      fetchWidgetData();
    }, [currentDate]);

    const headerItems: HeaderRowItem<BudgetPlanColumnType>[] = [
      {
        id: BudgetPlanColumnType.Metric,
        content: (
          <Typography>{t(commonTranslations.metric.key, commonTranslations.metric.defaultValue)}</Typography>
        ),
      },
      {
        id: BudgetPlanColumnType.Budget,
        content: (
          <Typography>
            {t(homeTranslations.budgetPlanned.budget.key, homeTranslations.budgetPlanned.budget.defaultValue)}
          </Typography>
        ),
      },
      {
        id: BudgetPlanColumnType.Plan,
        content: (
          <Typography>
            {t(homeTranslations.budgetPlanned.planned.key, homeTranslations.budgetPlanned.planned.defaultValue)}
          </Typography>
        ),
      },
    ];

    const transformedRows: BudgetPlanRow[] = [
      {
        metricName: homeTranslations.conversionRate.totalVisitors,
        budget: formatAmountWithSeparator(budgetPlanOverviewData?.conversionRateBudget ?? 0),
        plan: formatAmountWithSeparator(budgetPlanOverviewData?.conversionRatePlan ?? 0),
      },
      {
        metricName: soldProductsTranslations.salesNet,
        budget: getFormattedCurrency(budgetPlanOverviewData?.netSalesBudget ?? 0, { showCurrencyCode: false }),
        plan: getFormattedCurrency(budgetPlanOverviewData?.netSalesPlan ?? 0, { showCurrencyCode: false }),
      },
      {
        metricName: homeTranslations.efficiencyWidget.effectiveHours.title,
        budget: formatAmountWithSeparator(budgetPlanOverviewData?.effectiveHoursBudget ?? 0),
        plan: formatAmountWithSeparator(budgetPlanOverviewData?.effectiveHoursPlan ?? 0),
      },
      {
        metricName: homeTranslations.efficiencyWidget.takt1.title,
        budget: getFormattedCurrency(budgetPlanOverviewData?.takt1Budget ?? 0, { showCurrencyCode: false }),
        plan: getFormattedCurrency(budgetPlanOverviewData?.takt1Plan ?? 0, { showCurrencyCode: false }),
      },
      {
        metricName: homeTranslations.efficiencyWidget.spt.title,
        budget: formatAmountWithSeparator(budgetPlanOverviewData?.sptBudget ?? 0),
        plan: formatAmountWithSeparator(budgetPlanOverviewData?.sptPlan ?? 0),
      },
    ];

    return (
      <OverviewWidget
        id={AreaWidgetType.BudgetPlanOverview}
        isLoading={budgetPlanOverviewLoading}
        isError={budgetPlanOverviewError}
        errorRetryHandler={() => {
          fetchWidgetData();
        }}
        title={t(
          areaOverviewTranslations.budgetPlanOverview.title.key,
          areaOverviewTranslations.budgetPlanOverview.title.defaultValue,
        )}
        headerIcon={Icons.DOLLAR}
        customContent={
          <ColumnFlexContainer>
            <Typography>
              {t(
                areaOverviewTranslations.budgetPlanOverview.widgetText.key,
                areaOverviewTranslations.budgetPlanOverview.widgetText.defaultValue,
              )}
            </Typography>
            <ColumnFlexContainer data-testid="budget-plan-table" flex={1} padding={[2, 0, 0]} maxHeight={[56]}>
              <Table
                items={transformedRows}
                headerItems={headerItems}
                cellSize="small"
                getRowItems={(stockItem: BudgetPlanRow) => {
                  return [
                    {
                      id: BudgetPlanColumnType.Metric,
                      width: '40%',
                      content: (
                        <Typography type="body3">
                          {t(stockItem.metricName.key, stockItem.metricName.defaultValue)}
                        </Typography>
                      ),
                    },
                    {
                      id: BudgetPlanColumnType.Budget,
                      content: <Typography type="caption">{stockItem.budget}</Typography>,
                    },
                    {
                      id: BudgetPlanColumnType.Plan,
                      content: <Typography type="caption">{stockItem.plan}</Typography>,
                    },
                  ];
                }}
                stickyHeader
              />
            </ColumnFlexContainer>
          </ColumnFlexContainer>
        }
      />
    );
  },
);
